// src/pages/FundPlanner/utils/structureImport.js

function trimToNull(value) {
  const s = String(value ?? "").trim();
  return s || null;
}

function toNumberOrNull(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatYyyyMmDdCompact(dateStr) {
  const s = String(dateStr ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.replaceAll("-", "") : "";
}

function formatRateForId(ratePct) {
  const n = Number(ratePct ?? 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function buildCashIdentifier() {
  return "CASH";
}

function buildSlgsIdentifier(maturityDate, ratePct) {
  const yyyymmdd = formatYyyyMmDdCompact(maturityDate);
  const rate = formatRateForId(ratePct);
  return `SLGS-${yyyymmdd}-${rate}`;
}

function mapInvestmentType(invType) {
  const t = String(invType ?? "").trim().toUpperCase();
  if (t === "CASH") return "Cash";
  if (t === "SLGS") return "SLGS Time Deposit";
  return trimToNull(invType) || "";
}

function buildCusipOrId(inv) {
  const t = String(inv?.type ?? "").trim().toUpperCase();

  if (t === "CASH") return buildCashIdentifier();
  if (t === "SLGS") return buildSlgsIdentifier(inv?.maturityDate, inv?.ratePct);

  return trimToNull(inv?.id) || "";
}

function buildImportNote(structureName) {
  const name = String(structureName ?? "").trim();
  return name ? `Imported from FP Structurer: ${name}` : "Imported from FP Structurer";
}

function getNormalizedPurchasePrice(inv) {
  const rawType = String(inv?.type ?? "").trim().toUpperCase();
  const price = toNumberOrNull(inv?.price);

  if (rawType === "SLGS" && price == null) {
    return 100;
  }

  return price;
}

function warnIfUnexpectedSlgsPrice(inv) {
  const rawType = String(inv?.type ?? "").trim().toUpperCase();
  if (rawType !== "SLGS") return;

  const rawPrice = toNumberOrNull(inv?.price);
  const normalizedPrice = getNormalizedPurchasePrice(inv);

  if (rawPrice == null) {
    // eslint-disable-next-line no-console
    console.warn("[structureImport] Missing SLGS price in solution data, defaulting to 100", {
      id: inv?.id ?? null,
      name: inv?.name ?? null,
      maturityDate: inv?.maturityDate ?? null,
      ratePct: inv?.ratePct ?? null,
    });
    return;
  }

  if (normalizedPrice !== 100) {
    // eslint-disable-next-line no-console
    console.warn("[structureImport] Unexpected SLGS price in solution data", {
      id: inv?.id ?? null,
      name: inv?.name ?? null,
      price: normalizedPrice,
      maturityDate: inv?.maturityDate ?? null,
      ratePct: inv?.ratePct ?? null,
    });
  }
}

/* ────────────────────────────────
   Public API
──────────────────────────────── */

function hasHypotheticalPortfolio(structure) {
  const solution = structure?.solution || {};
  return Boolean(
    solution?.isHypothetical ||
      solution?.hypotheticalPortfolio?.isHypothetical ||
      solution?.usTreasury?.isHypothetical ||
      solution?.usAgency?.isHypothetical ||
      solution?.customCurve?.isHypothetical ||
      solution?.methodology === "us-treasury" ||
      solution?.methodology === "us-agencies" ||
      solution?.methodology === "custom-curve"
  );
}

export function isImportableStructure(structure) {
  if (hasHypotheticalPortfolio(structure)) return false;
  const investments = structure?.solution?.investments;
  return Array.isArray(investments) && investments.length > 0;
}

export function getStructureFundDefaults(structure) {
  const name = trimToNull(structure?.name);

  return {
    description: name
      ? `Imported from FP Structurer solution: ${name}`
      : "Imported from FP Structurer solution",
  };
}

export function getImportPreviewRows(structure) {
  if (hasHypotheticalPortfolio(structure)) return [];
  const investments = Array.isArray(structure?.solution?.investments)
    ? structure.solution.investments
    : [];

  return investments.map((inv, idx) => {
    warnIfUnexpectedSlgsPrice(inv);

    return {
      id: inv?.id || `row-${idx + 1}`,
      investment_type: mapInvestmentType(inv?.type),
      cusip_or_id: buildCusipOrId(inv),
      raw_security_description: trimToNull(inv?.name),
      par_amount: toNumberOrNull(inv?.principal),
      purchase_price: getNormalizedPurchasePrice(inv),
      raw_coupon:
        String(inv?.type ?? "").trim().toUpperCase() === "CASH"
          ? 0
          : toNumberOrNull(inv?.ratePct),
      raw_maturity_date: trimToNull(inv?.maturityDate),
      raw_security_type: trimToNull(inv?.type),
      external_position_id: trimToNull(inv?.id),
      original_cost: toNumberOrNull(inv?.cost),
      current_face: toNumberOrNull(inv?.principal),
    };
  });
}

export function translateStructureToPositionDrafts(structure, opts = {}) {
  if (hasHypotheticalPortfolio(structure)) return [];
  const {
    orgId = structure?.org_id ?? null,
    tenantId = structure?.tenant_id ?? null,
    fundId = null,
    sourceType = "structurer_import",
  } = opts;

  const structureName = String(structure?.name ?? "").trim();
  const purchaseDate = trimToNull(structure?.form?.asOfDate);
  const settlementDate = trimToNull(structure?.form?.settlementDate);
  const investments = Array.isArray(structure?.solution?.investments)
    ? structure.solution.investments
    : [];

  return investments.map((inv) => {
    warnIfUnexpectedSlgsPrice(inv);

    const rawType = String(inv?.type ?? "").trim().toUpperCase();
    const coupon = rawType === "CASH" ? 0 : toNumberOrNull(inv?.ratePct);

    return {
      org_id: orgId,
      tenant_id: tenantId,
      fund_id: fundId,

      source_type: sourceType,
      source_batch_id: null,
      source_file_row_num: null,

      external_position_id: trimToNull(inv?.id),
      lot_id: null,

      cusip_or_id: buildCusipOrId(inv),

      purchase_date: purchaseDate,
      settlement_date: settlementDate,
      closed_date: null,

      par_amount: toNumberOrNull(inv?.principal),
      current_face: toNumberOrNull(inv?.principal),
      units: null,

      purchase_price: getNormalizedPurchasePrice(inv),
      original_cost: toNumberOrNull(inv?.cost),
      book_value_input: null,
      market_value_input: null,

      account_name: null,
      account_number: null,
      fund_account_code: null,

      raw_security_description: trimToNull(inv?.name),
      raw_security_type: trimToNull(inv?.type),
      raw_coupon: coupon,
      raw_maturity_date: trimToNull(inv?.maturityDate),
      raw_issuer_name: null,

      is_active: true,
      notes: buildImportNote(structureName),
      investment_type: mapInvestmentType(inv?.type),
    };
  });
}

export function findDuplicateImportedPositions(existingRows, importedRows) {
  const existingKeys = new Set(
    (Array.isArray(existingRows) ? existingRows : []).map((row) =>
      [
        String(row?.source_type ?? ""),
        String(row?.external_position_id ?? ""),
        String(row?.purchase_date ?? ""),
      ].join("|")
    )
  );

  return (Array.isArray(importedRows) ? importedRows : []).filter((row) =>
    existingKeys.has(
      [
        String(row?.source_type ?? ""),
        String(row?.external_position_id ?? ""),
        String(row?.purchase_date ?? ""),
      ].join("|")
    )
  );
}
